import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import ApiClient from '../../utils/apiClient';
import { clearUserCache, clearConsumptionCache } from '../../utils/cacheUtils';
import { startOctopusLoadSse } from '../../utils/loadOctopusSse';

interface EditDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  userInfo: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  accountData: {
    account_number: string;
    octopus_api_key: string;
  } | null;
  onSave: (updatedData: {
    first_name: string;
    last_name: string;
    email: string;
    octopus_account_number: string;
    octopus_api_key: string;
  }) => void;
  onOctopusCredentialsChanged?: (apiKey: string, accountNumber: string) => void;
}

export default function EditDetailsDialog({ open, onClose, userInfo, accountData, onSave, onOctopusCredentialsChanged }: EditDetailsDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [editFormData, setEditFormData] = React.useState({
    first_name: '',
    last_name: '',
    email: '',
    octopus_account_number: '',
    octopus_api_key: ''
  });
  const [editError, setEditError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [initialFormData, setInitialFormData] = React.useState({
    first_name: '',
    last_name: '',
    email: '',
    octopus_account_number: '',
    octopus_api_key: ''
  });

  // Initialize form data when dialog opens
  React.useEffect(() => {
    if (open && userInfo && accountData) {
      const initialData = {
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        email: userInfo.email,
        octopus_account_number: accountData.account_number,
        octopus_api_key: ''
      };
      setEditFormData(initialData);
      setInitialFormData(initialData);
      setEditError(null);
    }
  }, [open, userInfo, accountData]);

  // Check if form has been modified
  const hasChanges = React.useMemo(() => {
    return editFormData.first_name.trim() !== initialFormData.first_name ||
           editFormData.last_name.trim() !== initialFormData.last_name ||
           editFormData.email.trim() !== initialFormData.email ||
           editFormData.octopus_account_number.trim() !== initialFormData.octopus_account_number ||
           editFormData.octopus_api_key.trim() !== '';
  }, [editFormData, initialFormData]);

  const handleInputChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setEditError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setEditError('Authentication token not found');
        setSaving(false);
        return;
      }

      // Determine which Octopus credential flow to use
      const accountNumberChanged =
        !!editFormData.octopus_account_number &&
        editFormData.octopus_account_number !== accountData?.account_number;
      const newApiKeySupplied =
        !!editFormData.octopus_api_key && editFormData.octopus_api_key.trim() !== '';

      const data = await ApiClient.put('api/users/current', {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        email: editFormData.email
      });

      if (data.success) {
        // Clear user cache since details have been updated
        clearUserCache();
        onSave(editFormData);

        // 1) Account number changed: full delete + reconnect flow
        if (accountNumberChanged) {
          try {
            clearConsumptionCache();
            const deleteReadingsResponse = await ApiClient.delete('api/connect/load/octopus');

            if (deleteReadingsResponse.success) {
              const deleteOctopusResponse = await ApiClient.delete('api/connect/octopus');
              if (deleteOctopusResponse.success && onOctopusCredentialsChanged) {
                const updatedApiKey = editFormData.octopus_api_key.trim() || accountData?.octopus_api_key || '';
                const updatedAccountNumber = editFormData.octopus_account_number || accountData?.account_number || '';
                onOctopusCredentialsChanged(updatedApiKey, updatedAccountNumber);
              }
            }
          } catch (error) {
            console.error('Error deleting Octopus connection:', error);
            clearConsumptionCache();
            if (onOctopusCredentialsChanged) {
              const updatedApiKey = editFormData.octopus_api_key.trim() || accountData?.octopus_api_key || '';
              const updatedAccountNumber = editFormData.octopus_account_number || accountData?.account_number || '';
              onOctopusCredentialsChanged(updatedApiKey, updatedAccountNumber);
            }
          }
          handleClose();
        }
        // 2) Account number unchanged but new (different) API key: update key then reload via SSE
        else if (newApiKeySupplied) {
          try {
            const updateKeyResponse = await ApiClient.put('api/connect/octopus', {
              apiKey: editFormData.octopus_api_key.trim()
            });
            if (!updateKeyResponse?.success) {
              setEditError(updateKeyResponse?.msg || "Couldn't connect to Octopus Energy. Please check the account nummber and API key and try again.");
              return;
            }
          } catch (error) {
            setEditError(error instanceof Error ? (error as any).message ?? error.message : "Couldn't connect to Octopus Energy. Please check the account nummber and API key and try again.");
            return;
          }
          clearConsumptionCache();
          const userId = localStorage.getItem('user_id');
          if (userId) {
            startOctopusLoadSse(userId);
          }
          handleClose();
        }
        // 3) Account number unchanged and no new API key (or same as existing): do nothing
        else {
          handleClose();
        }
      } else {
        setEditError(data.msg || 'Failed to update user details');
   }
    } catch (error) {
      // Check if this is a 400 error with a message
      if (error instanceof Error && (error as any).status === 400) {
        // Try to extract the message from the error
        try {
          const errorMessage = (error as any).message || 'Failed to update user details';
          setEditError(errorMessage);
        } catch {
          setEditError('Failed to update user details');
        }
      } else {
        setEditError('Network error occurred while updating details');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setEditFormData({
      first_name: '',
      last_name: '',
      email: '',
      octopus_account_number: '',
      octopus_api_key: ''
    });
    setEditError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-details-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="edit-details-dialog-title">
        Edit Account Details
      </DialogTitle>
      <DialogContent>
        {editError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {editError}
          </Alert>
        )}
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: '8px 16px 8px 0', width: isMobile ? '100px' : '200px' }}>
                  <Typography variant="body1">First Name:</Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', padding: '8px 0' }}>
                  <TextField
                    value={editFormData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    fullWidth
                    required
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: '8px 16px 8px 0', width: isMobile ? '100px' : '200px' }}>
                  <Typography variant="body1">Last Name:</Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', padding: '8px 0' }}>
                  <TextField
                    value={editFormData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    fullWidth
                    required
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: '8px 16px 8px 0', width: isMobile ? '100px' : '200px' }}>
                  <Typography variant="body1">Email:</Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', padding: '8px 0' }}>
                  <TextField
                    value={editFormData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    fullWidth
                    required
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: '8px 16px 8px 0', width: isMobile ? '100px' : '200px' }}>
                  <Typography variant="body1">Octopus Account:</Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', padding: '8px 0' }}>
                  <TextField
                    value={editFormData.octopus_account_number}
                    onChange={(e) => handleInputChange('octopus_account_number', e.target.value)}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: 'none', padding: '8px 16px 8px 0', width: isMobile ? '100px' : '200px' }}>
                  <Typography variant="body1">Octopus API Key:</Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', padding: '8px 0' }}>
                  <TextField
                    value={editFormData.octopus_api_key}
                    onChange={(e) => handleInputChange('octopus_api_key', e.target.value)}
                    fullWidth
                    placeholder={accountData?.octopus_api_key || ''}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={saving}
          sx={{ 
            color: theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary',
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={saving || !hasChanges}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
